import { PermissionEnum } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import StepOne from '../../../../components/new-application/individual/step-one';
import StepTwo from '../../../../components/new-application/individual/step-two';
import StepThree from '../../../../components/new-application/individual/step-three';
import Agreements from '../../../../components/new-application/common/agreements/agreements';
import StepItem from '../../../../components/new-application/common/steps/step-item';
import OngoingMonitoring from '../../common/ongoing-monitoring';
import { NewApplicationStepStatus } from '../../types';
import { onGroupSelect, setSelected } from '../store/slices';
import { ApplicationStepsTypes } from './types';

const ApplicationSteps = ({
  applicationStepsStatus,
  control,
  questionnarieControl,
  errors,
  questionnarieErrors,
  newApplicationInfo,
  fields,
  activePermissions,
  clearErrors,
  questionnarieClearErrors,
  questionnarieFields,
  sourceOfWealth,
  questionnarieSetValue,
  sourceOfIncome,
  sourceOfWealthForIndividual,
  documents,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
  customerSourceOfFunds,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
  search,
  applicationId,
  selectedCrsAndFatcaId,
  crsAndFatcaIsLoading,
  crsAndFatcaIsFetching,
  deleteCrsAndFatcaInProgress,
  onDeleteCRSAndFatca,
  crsAndFatcaValues,
  setCrsAndFatcaValues,
  setHaveSecondPassport,
  haveSecondPassport,
  setValue,
  ongoingMonitoring,
  ongoingCheckingStatus,
  agreements,
  agreementsIsLoading,
  infoDictionariesIsLoading,
  questionnarieIsLoading,
  agreementsErrorsList,
}: ApplicationStepsTypes) => {
  const dispatch = useAppDispatch();
  const {
    countriesList: countries,
    baseCurrency,
    currenciesList,
    currencies,
  } = useAppSelector((state) => state.root);
  const { allowedDocumentsCategories, selectedDocumentsCounts } =
    useAppSelector((state) => state.newApplications.individualInfo);

  return (
    <>
      <StepItem
        title="Step 1"
        status={applicationStepsStatus?.stepOne as NewApplicationStepStatus}
        id="step-1"
        zIndex={10}
      >
        <StepOne
          control={control}
          questionnarieControl={questionnarieControl}
          errors={errors}
          questionnarieErrors={questionnarieErrors}
          status={newApplicationInfo?.status}
          clientActivity={fields?.clientActivityId}
          sourceOfFundsIds={fields?.sourceOfFundsIds}
          isEditable={
            activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            ) && newApplicationInfo?.basePersonalInfoEditable
          }
          hasSumSubId={Boolean(
            newApplicationInfo?.sumSubApplication?.applicantId,
          )}
          activePermissions={activePermissions}
          applicationScheme={newApplicationInfo?.applicationScheme}
          clearErrors={clearErrors}
          questionnarieClearErrors={questionnarieClearErrors}
          regulatedCompanyScheme={
            newApplicationInfo?.applicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a'
          }
          questionnarieFields={questionnarieFields}
          sourceOfWealth={sourceOfWealth}
          baseCurrency={baseCurrency}
          questionnarieSetValue={questionnarieSetValue}
          sourceOfIncome={sourceOfIncome}
          sourceOfWealthForIndividual={sourceOfWealthForIndividual}
          fields={fields}
          groupStructurChartFile={documents?.find(
            (item) =>
              item.documentCategoryId ===
              '72b810fc-d635-4937-8a14-f9b77eaa699f',
          )}
          questionnarieIsLoading={questionnarieIsLoading}
        />
      </StepItem>
      <StepItem
        title="Step 2"
        status={applicationStepsStatus?.stepTwo as NewApplicationStepStatus}
        id="step-2"
        zIndex={9}
      >
        <StepTwo
          control={control}
          status={newApplicationInfo?.status}
          isEditable={activePermissions?.includes(
            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
          )}
          errors={errors}
          clearErrors={clearErrors}
          questionnarieClearErrors={questionnarieClearErrors}
          questionnarieControl={questionnarieControl}
          questionnarieErrors={questionnarieErrors}
          questionnarieSetValue={questionnarieSetValue}
          questionnarieFields={questionnarieFields}
          accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
          accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
          currencies={currenciesList}
          accountsCurrenciesValues={accountsCurrenciesValues}
          setAccountsCurrenciesValues={setAccountsCurrenciesValues}
          onDeleteAccountCurrencies={onDeleteAccountCurrencies}
          selectedAccountsCurrencyId={selectedAccountsCurrencyId}
          deleteAccountsCurrenciesInProgress={
            deleteAccountsCurrenciesInProgress
          }
          setAccountsFlowsValues={setAccountsFlowsValues}
          accountsFlowsValues={accountsFlowsValues}
          customerSourceOfFunds={customerSourceOfFunds}
          regulatedCompanyScheme={
            newApplicationInfo?.applicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a'
          }
          accountsFlowsIsLoading={accountsFlowsIsLoading}
          accountsFlowsIsFetching={accountsFlowsIsFetching}
          currenciesList={currencies}
          expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
          expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
          deleteExpectedFundsOriginsInProgress={
            deleteExpectedFundsOriginsInProgress
          }
          onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
          selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
          expectedFundsOriginsValues={expectedFundsOriginsValues}
          setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
          countries={countries}
          infoDictionariesIsLoading={infoDictionariesIsLoading}
          questionnarieIsLoading={questionnarieIsLoading}
        />
      </StepItem>
      {(!newApplicationInfo?.applicationScheme ||
        (newApplicationInfo.applicationScheme &&
          newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')) && (
        <StepItem
          title="Step 3"
          status={applicationStepsStatus?.stepThree as NewApplicationStepStatus}
          id="step-3"
          zIndex={8}
        >
          <StepThree
            sumSubStatus={newApplicationInfo?.sumSubApplication?.reviewAnswer}
            sumSubReviewStatus={
              newApplicationInfo?.sumSubApplication?.reviewStatus
            }
            id={newApplicationInfo?.sumSubApplication?.applicantId as string}
            changeLogsAvailable={activePermissions?.includes(
              PermissionEnum.AUDIT_LOG_LIST,
            )}
            sumSubId={newApplicationInfo?.sumSubApplication?.id}
            search={search}
            onGroupSelect={(data) => dispatch(onGroupSelect(data))}
            countries={countries}
            control={control}
            documents={documents}
            selectedDocumentsCounts={selectedDocumentsCounts}
            status={newApplicationInfo?.status}
            applicationId={applicationId}
            documentsVisible={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
            )}
            isEditable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            )}
            setSelected={({ id, selected }) =>
              dispatch(setSelected({ id, selected }))
            }
            sumSubDocuments={newApplicationInfo?.idDocs?.edges?.map(
              (item) => item.node,
            )}
            errors={errors}
            renameAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
            )}
            regulatedCompanyScheme={
              newApplicationInfo?.applicationScheme?.id ===
              'ed560559-8354-4634-b6dd-eb341dfb551a'
            }
            clearErrors={clearErrors}
            fields={fields}
            questionnarieControl={questionnarieControl}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieErrors={questionnarieErrors}
            questionnarieFields={questionnarieFields}
            questionnarieSetValue={questionnarieSetValue}
            selectedCrsAndFatcaId={selectedCrsAndFatcaId}
            crsAndFatcaIsLoading={crsAndFatcaIsLoading}
            crsAndFatcaIsFetching={crsAndFatcaIsFetching}
            deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
            onDeleteCRSAndFatca={onDeleteCRSAndFatca}
            crsAndFatcaValues={crsAndFatcaValues}
            setCrsAndFatcaValues={setCrsAndFatcaValues}
            setHaveSecondPassport={setHaveSecondPassport}
            haveSecondPassport={haveSecondPassport}
            setValue={setValue}
            allowedDocumentsCategories={allowedDocumentsCategories}
            activePermissions={activePermissions}
            questionnarieIsLoading={questionnarieIsLoading}
          />
        </StepItem>
      )}

      <StepItem
        title={
          !newApplicationInfo?.applicationScheme ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
            ? 'Step 4'
            : 'Step 3'
        }
        status={applicationStepsStatus?.stepFive as NewApplicationStepStatus}
        id="step-4"
        zIndex={7}
      >
        <OngoingMonitoring
          status={newApplicationInfo?.status}
          applicationId={newApplicationInfo?.id}
          ongoingMonitoring={ongoingMonitoring}
          ongoingCheckingStatus={ongoingCheckingStatus}
        />
      </StepItem>
      {(!newApplicationInfo?.applicationScheme ||
        (newApplicationInfo.applicationScheme &&
          newApplicationInfo.applicationScheme.agreementsActive) ||
        (newApplicationInfo.applicationScheme &&
          newApplicationInfo.applicationScheme.agreementsActive === null)) && (
        <StepItem
          title={
            !newApplicationInfo?.applicationScheme ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
              ? 'Step 5'
              : 'Step 4'
          }
          status={applicationStepsStatus?.stepFour as NewApplicationStepStatus}
          id="step-5"
          zIndex={6}
        >
          <Agreements
            list={agreements}
            errors={errors}
            zIndex={17}
            agreementsIsLoading={agreementsIsLoading}
            activePermissions={activePermissions}
            agreementsErrorsList={agreementsErrorsList}
          />
        </StepItem>
      )}
    </>
  );
};

export default ApplicationSteps;
